// ═══════════════ PATRIMOINE - FORMATTING & AMORTIZATION HELPERS ═══════════════
// Pure helpers extracted from PatrimoinePage.js.

export const fmt = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

export const fmtD = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export const fmtP = (v) => `${v >= 0 ? '+' : ''}${(v || 0).toFixed(2)}%`;

// Standard amortization schedule (constant monthly payment) with optional early repayments.
// `prepayments` is an array of { amount: number, date: "YYYY-MM-DD" } items — each is
// applied to the remaining capital at the month containing its date. Regular payments are
// counted only for fully-elapsed months (0..monthsElapsed-1); a prepayment dated in the
// ONGOING month (idx === monthsElapsed) is still subtracted from the displayed remaining
// capital so the user sees its impact immediately.
// Returns: { capitalRembourse, capitalRestant, moisRestants, totalInterets, pctRembourse, totalMonths }
export const computeAmortization = (totalAmount, monthlyPayment, annualRate, startDate, prepayments = []) => {
  if (!totalAmount || !monthlyPayment) {
    return { capitalRembourse: 0, capitalRestant: totalAmount || 0, moisRestants: 0, totalInterets: 0, pctRembourse: 0, totalMonths: 0 };
  }
  const mr = (annualRate || 0) / 100 / 12;
  const start = startDate ? new Date(startDate) : null;
  const now = new Date();
  const monthsElapsed = start
    ? Math.max(0, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()))
    : 0;

  // Build a map of prepayments by month index relative to start_date.
  const prepayByMonth = new Map();
  if (start) {
    (prepayments || []).forEach(p => {
      const amt = parseFloat(p?.amount) || 0;
      if (!p?.date || amt <= 0) return;
      const d = new Date(p.date);
      if (Number.isNaN(d.getTime())) return;
      const idx = (d.getFullYear() - start.getFullYear()) * 12 + (d.getMonth() - start.getMonth());
      if (idx >= 0) prepayByMonth.set(idx, (prepayByMonth.get(idx) || 0) + amt);
    });
  }

  // ── Pass 1: current state ──
  // Simulate `monthsElapsed` regular payments, applying any prepayments scheduled within
  // those months. Then subtract any prepayment dated in the ongoing month (idx ===
  // monthsElapsed) so a freshly-recorded prepayment shows up immediately.
  let remCurrent = totalAmount;
  for (let i = 0; i < monthsElapsed && remCurrent > 0.01; i++) {
    const interest = remCurrent * mr;
    const principal = monthlyPayment - interest;
    if (principal > 0) remCurrent = Math.max(0, remCurrent - principal);
    if (prepayByMonth.has(i)) remCurrent = Math.max(0, remCurrent - prepayByMonth.get(i));
  }
  if (prepayByMonth.has(monthsElapsed)) {
    remCurrent = Math.max(0, remCurrent - prepayByMonth.get(monthsElapsed));
  }

  // ── Pass 2: full schedule ──
  // Simulate until the loan is fully paid to derive totalMonths and totalInterest.
  let remFull = totalAmount;
  let totalInterest = 0;
  let totalMonths = 0;
  while (remFull > 0.01 && totalMonths < 600) {
    const interest = remFull * mr;
    const principal = monthlyPayment - interest;
    const hasPrepayment = prepayByMonth.has(totalMonths);
    if (principal <= 0 && !hasPrepayment) { totalMonths = 9999; break; }
    if (principal > 0) remFull = Math.max(0, remFull - principal);
    if (hasPrepayment) remFull = Math.max(0, remFull - prepayByMonth.get(totalMonths));
    totalInterest += interest;
    totalMonths++;
  }

  return {
    capitalRembourse: totalAmount - remCurrent,
    capitalRestant: remCurrent,
    moisRestants: Math.max(0, totalMonths - monthsElapsed),
    totalInterets: totalInterest,
    pctRembourse: ((totalAmount - remCurrent) / totalAmount) * 100,
    totalMonths,
  };
};
